import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { employeesApi, unifiedProjectsApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  Target,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  FolderOpen,
  FileText,
  Building2,
  ExternalLink,
  CheckCircle2,
  Clock,
  DollarSign,
  TrendingUp,
  Plus,
  Trash2,
  UserX,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  Wrench,
  CircleDot,
  Info,
} from "lucide-react";
import CreateProjectDialog from "../components/CreateProjectDialog";

// Generate month options for dropdown
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = -3; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { year: "numeric", month: "short" });
    months.push({ value, label });
  }
  return months;
};

const DEAL_STATUSES = [
  // Sales statuses
  { value: "proposal_sent", label: "Proposal", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  { value: "contract_negotiation", label: "Negotiation", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { value: "contract_signed", label: "Signed", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  // PM statuses
  { value: "sent_to_pool", label: "In Pool", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  { value: "in_progress", label: "In Progress", color: "bg-sky-500/20 text-sky-400 border-sky-500/30" },
  { value: "awaiting_client", label: "Awaiting Client", color: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
  { value: "awaiting_payment", label: "Awaiting Payment", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  { value: "completed", label: "Completed", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  // Legacy
  { value: "active", label: "Active", color: "bg-sky-500/20 text-sky-400 border-sky-500/30" },
];

// Konstantin special manager ID
const KONSTANTIN_ID = "konstantin-kuzmenko-id";

// Marker: realistic-forecast-v1 (sales/production phase split — see CLAUDE.md)
// viewMode "realistic" = default. Filters by computed forecast month (production
// signals replace manager-set month for production-phase payments).
// viewMode "all" = legacy. Filters strictly by manager-set service.month.
// Sticky default "realistic" on every mount — by design (manager shouldn't
// linger in wishful-mode just because they toggled All once).
const VIEW_MODES = [
  { value: "realistic", label: "Realistic Forecast" },
  { value: "all", label: "All Plan" },
];

const SalesDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { displayCurrency, getCurrencySymbol, rates } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("realistic");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [salesManagers, setSalesManagers] = useState([]);
  const [pmManagers, setPmManagers] = useState([]); // Project managers
  const [currentEmployee, setCurrentEmployee] = useState(null); // For filtering own projects
  const [expandedManagers, setExpandedManagers] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});
  
  // New project dialog state
  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  // Client column sort: null | "asc" | "desc"
  const [clientSort, setClientSort] = useState(null);
  const toggleClientSort = useCallback(() => {
    setClientSort(prev => prev === null ? "asc" : prev === "asc" ? "desc" : null);
  }, []);
  const usdToUah = rates?.USD || 41.5;
  const eurToUah = rates?.EUR || 43.5;
  
  // Month options for service month dropdown
  const monthOptions = useMemo(() => getMonthOptions(), []);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, viewMode]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // viewMode=realistic → backend filters by computed forecast month
      // viewMode=all → backend filters by manager-set service.month (legacy)
      const servicesUrl = viewMode === "realistic"
        ? `/sales-services?forecast_month=${selectedMonth}`
        : `/sales-services?month=${selectedMonth}`;
      const [projectsRes, servicesRes, empRes, clientsRes] = await Promise.all([
        unifiedProjectsApi.getAll(),
        api.get(servicesUrl),
        employeesApi.getAll(),
        api.get(`/sales-clients`),
      ]);
      
      setProjects(projectsRes.data || []);
      setServices(servicesRes.data || []);
      setClients(clientsRes.data || []);
      
      const allEmployees = empRes.data || [];
      const sales = allEmployees.filter(e => e.position === "sales" && e.is_active);
      setSalesManagers(sales);
      
      // PM managers - position is "project_manager" or any team member who can manage projects
      const pms = allEmployees.filter(e => 
        (e.position === "project_manager" || e.position === "admin") && e.is_active
      );
      setPmManagers(pms);
      
      // Find current user's employee profile by email
      const myEmployee = allEmployees.find(e => e.email === user?.email);
      setCurrentEmployee(myEmployee);
      
      // Auto-expand all managers
      const expanded = {};
      sales.forEach(s => { expanded[s.id] = true; });
      setExpandedManagers(expanded);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const toUAH = (amount, currency) => {
    if (!amount) return 0;
    if (currency === "UAH") return amount;
    if (currency === "USD") return amount * usdToUah;
    if (currency === "EUR") return amount * eurToUah;
    return amount;
  };

  const formatAmount = (amountUAH) => {
    let converted = amountUAH;
    if (displayCurrency === "USD") converted = amountUAH / usdToUah;
    else if (displayCurrency === "EUR") converted = amountUAH / eurToUah;
    const symbol = getCurrencySymbol();
    return `${symbol}${converted.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const formatOriginal = (amount, currency) => {
    const symbols = { USD: "$", EUR: "€", UAH: "₴" };
    return `${amount.toLocaleString()} ${symbols[currency] || currency}`;
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${monthNames[month - 1]} ${year}`;
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split("-").map(Number);
    let newMonth = month + direction;
    let newYear = year;
    if (newMonth > 12) { newMonth = 1; newYear++; }
    else if (newMonth < 1) { newMonth = 12; newYear--; }
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
  };

  const getProjectManager = (project) => {
    // New projects have sales_manager_id directly
    if (project.sales_manager_id) {
      return project.sales_manager_id;
    }
    // Old projects get manager from client
    const client = clients.find(c => c.id === project.client_id);
    return client?.sales_manager_id;
  };

  // Delete project using unified API
  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Delete project "${projectName}"? This will also delete all its services.`)) {
      return;
    }
    try {
      await unifiedProjectsApi.delete(projectId);
      toast.success("Project deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  // Toggle service paid status
  const toggleServicePaid = async (serviceId, isPaid) => {
    try {
      const res = await api.put(`/sales-services/${serviceId}`, {
        is_paid: isPaid,
        paid_date: isPaid ? new Date().toISOString().split("T")[0] : null,
      });
      setServices(prev => prev.map(s => s.id === serviceId ? { ...s, ...res.data } : s));
      toast.success(isPaid ? "Marked as paid" : "Marked as unpaid");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Update service month
  const updateServiceMonth = async (serviceId, newMonth) => {
    try {
      const res = await api.put(`/sales-services/${serviceId}`, { month: newMonth });
      setServices(prev => {
        // If moved out of currently viewed month, drop it from the list
        if (res.data?.month && res.data.month !== selectedMonth) {
          return prev.filter(s => s.id !== serviceId);
        }
        return prev.map(s => s.id === serviceId ? { ...s, ...res.data } : s);
      });
      toast.success("Month updated");
    } catch (error) {
      toast.error("Failed to update month");
    }
  };

  // Update service probability.
  // PUT response is a basic SalesServiceResponse — missing the realistic-forecast
  // computed fields (payment_phase, realistic_forecast_month, …) that the list
  // endpoint adds. Optimistic merge `{...s, ...res.data}` would leave stale
  // probability_locked/probability in some Radix Select re-renders on the
  // dashboard (worked fine inside SalesProjectEditPage where we re-fetch).
  // Match that pattern here: PUT then re-fetch the full list with the current
  // view's filter so we see the same shape we render from.
  const updateServiceProbability = async (serviceId, newProbability) => {
    try {
      await api.put(`/sales-services/${serviceId}`, {
        probability: newProbability,
        probability_override: true  // Mark as manually set
      });
      const servicesUrl = viewMode === "realistic"
        ? `/sales-services?forecast_month=${selectedMonth}`
        : `/sales-services?month=${selectedMonth}`;
      const servicesRes = await api.get(servicesUrl);
      setServices(servicesRes.data || []);
      toast.success("Probability updated");
    } catch (error) {
      toast.error("Failed to update probability");
    }
  };

  // Group projects by manager with bonus calculations
  // Include projects that have services in the selected month OR were created in selected month
  const { projectsByManager, unassignedProjects, monthTotals } = useMemo(() => {
    const grouped = {};
    const unassigned = {
      manager: { id: "unassigned", name: "Unassigned / Inactive Manager" },
      activeProjects: [],
      closedProjects: [],
      totalPlanUAH: 0,
      totalFactUAH: 0,
      totalAllUAH: 0,
      servicesCount: 0,
      paidCount: 0,
    };
    
    salesManagers.forEach(m => {
      grouped[m.id] = {
        manager: m,
        activeProjects: [],    // Projects with unpaid services
        closedProjects: [],    // Projects with all services paid
        newProjects: [],       // Projects created this month without services
        totalPlanUAH: 0,
        totalFactUAH: 0,
        totalAllUAH: 0,
        servicesCount: 0,
        paidCount: 0,
      };
    });
    
    // Get unique project IDs that have services in this month
    const projectIdsWithServices = [...new Set(services.map(s => s.project_id))];
    
    // Also include projects created in this month (even without services)
    const projectsThisMonth = projects.filter(p => p.month === selectedMonth);
    const allProjectIds = [...new Set([...projectIdsWithServices, ...projectsThisMonth.map(p => p.id)])];
    
    // Get partnership manager IDs for filtering
    const partnershipIds = new Set(salesManagers.filter(m => m.is_partnership).map(m => m.id));
    
    allProjectIds.forEach(projectId => {
      const project = projects.find(p => p.id === projectId);
      if (!project) return;
      
      const managerId = getProjectManager(project);
      
      // Skip partnership projects for non-admin users
      if (user?.role !== "admin" && partnershipIds.has(managerId)) {
        return; // Don't count partnership projects at all
      }
      
      const targetGroup = (managerId && grouped[managerId]) ? grouped[managerId] : unassigned;
      
      // Get only services for this project in selected month
      const projectServices = services.filter(s => s.project_id === projectId);
      // Plan = high-probability OR paid (defensive — paid services should always
      // count even if probability wasn't manually bumped; backend floor auto-bumps
      // to 100 on paid, this OR is the safety net for any pre-backfill gap).
      const planServices = projectServices.filter(s => s.probability >= 50 || s.is_paid);
      const paidServices = projectServices.filter(s => s.is_paid);
      
      const planUAH = planServices.reduce((sum, s) => sum + toUAH(s.amount, s.currency), 0);
      const factUAH = paidServices.reduce((sum, s) => sum + toUAH(s.amount, s.currency), 0);
      const allUAH = projectServices.reduce((sum, s) => sum + toUAH(s.amount, s.currency), 0);
      
      const client = clients.find(c => c.id === project.client_id);
      
      // Check if all services are paid (closed project)
      const isClosed = projectServices.length > 0 && projectServices.every(s => s.is_paid);
      const hasNoServices = projectServices.length === 0;
      
      const projectData = {
        ...project,
        client_name: project.client_name || client?.name || "Unknown",
        country: project.country || client?.country || "",
        services: projectServices,
        planUAH,
        factUAH,
        allUAH,
        paidCount: paidServices.length,
        isClosed,
        hasNoServices,
      };
      
      // Add to appropriate list
      if (hasNoServices) {
        targetGroup.activeProjects.push(projectData); // Show new projects in active list
      } else if (isClosed) {
        targetGroup.closedProjects.push(projectData);
      } else {
        targetGroup.activeProjects.push(projectData);
      }
      
      // Always add to manager totals (both active and closed)
      targetGroup.totalPlanUAH += planUAH;
      targetGroup.totalFactUAH += factUAH;
      targetGroup.totalAllUAH += allUAH;
      targetGroup.servicesCount += projectServices.length;
      targetGroup.paidCount += paidServices.length;
    });
    
    const result = Object.values(grouped).filter(g => 
      g.activeProjects.length > 0 || g.closedProjects.length > 0
    );
    
    // Only include unassigned if it has projects
    const hasUnassigned = unassigned.activeProjects.length > 0 || unassigned.closedProjects.length > 0;
    
    // Filter based on user role
    let filteredResult = result;
    let showUnassigned = false;
    
    if (user?.role === "admin") {
      // Admin sees everything
      showUnassigned = hasUnassigned;
    } else if (user?.role === "sales_director") {
      // Sales director sees all EXCEPT partnership managers + unassigned
      filteredResult = result.filter(g => !g.manager.is_partnership);
      showUnassigned = hasUnassigned;
    } else if (user?.role === "team") {
      // Commercial director (team role) sees all EXCEPT partnership managers
      // BUT can see unassigned projects (they just don't count for bonus)
      filteredResult = result.filter(g => !g.manager.is_partnership);
      showUnassigned = hasUnassigned;
    } else if (user?.role === "sales") {
      // Sales managers see ONLY their own projects
      if (currentEmployee) {
        filteredResult = result.filter(g => g.manager.id === currentEmployee.id);
      } else {
        filteredResult = []; // No employee linked - show nothing
      }
      showUnassigned = false; // Sales don't see unassigned
    }
    
    // Calculate totals based on FILTERED results (what user actually sees)
    const visibleGroups = showUnassigned ? [...filteredResult, unassigned] : filteredResult;
    const visibleProjectIds = new Set();
    visibleGroups.forEach(g => {
      g.activeProjects.forEach(p => visibleProjectIds.add(p.id));
      g.closedProjects.forEach(p => visibleProjectIds.add(p.id));
    });
    const visibleServices = services.filter(s => visibleProjectIds.has(s.project_id));
    
    const pendingServices = visibleServices.filter(s => s.probability > 50 && !s.is_paid);
    const totals = {
      planUAH: visibleGroups.reduce((sum, g) => sum + g.totalPlanUAH, 0),
      factUAH: visibleGroups.reduce((sum, g) => sum + g.totalFactUAH, 0),
      allUAH: visibleGroups.reduce((sum, g) => sum + g.totalAllUAH, 0),
      pendingUAH: pendingServices.reduce((sum, s) => sum + toUAH(s.amount, s.currency), 0),
      pendingCount: pendingServices.length,
      projectsCount: visibleProjectIds.size,
      servicesCount: visibleServices.length,
      paidCount: visibleServices.filter(s => s.is_paid).length,
    };
    
    return { 
      projectsByManager: filteredResult, 
      unassignedProjects: showUnassigned ? unassigned : null,
      monthTotals: totals 
    };
  }, [projects, services, clients, salesManagers, user, currentEmployee]);

  // Calculate bonuses
  const bonusData = useMemo(() => {
    const result = {};
    
    // Filter out partnership managers for Kuzmenko's override calculation
    const salesManagersForOverride = projectsByManager
      .filter(g => g.manager.name !== "Kuzmenko Kostiantyn" && !g.manager.is_partnership);
    
    const totalOtherManagersFact = salesManagersForOverride
      .reduce((sum, g) => sum + g.totalFactUAH, 0);
    
    projectsByManager.forEach(g => {
      // Per-PM rate from staff.sales_bonus_percent — empty/null = no bonus
      // (uniform with backend: server.py get_sales_dashboard, generate_salary_payments, etc.)
      const rateRaw = g.manager?.sales_bonus_percent;
      const ratePct = rateRaw === null || rateRaw === undefined || rateRaw === "" ? 0 : Number(rateRaw);
      const rate = Number.isFinite(ratePct) && ratePct > 0 ? ratePct / 100 : 0;
      const expectedBonus = g.totalPlanUAH * rate;
      const realBonus = g.totalFactUAH * rate;
      
      let overrideBonus = 0;
      let overrideExpected = 0;
      
      // Konstantin special override (excludes partnership managers).
      // Rates from staff.override_bonus_rate_low/_high (defaults 4 / 6). Thresholds
      // ($10k / $50k peers) stay hardcoded — same as backend.
      if (g.manager.name === "Kuzmenko Kostiantyn") {
        const totalOtherManagersPlan = salesManagersForOverride
          .reduce((sum, m) => sum + m.totalPlanUAH, 0);

        const parseRate = (v, defaultPct) => {
          if (v === null || v === undefined || v === "") return defaultPct / 100;
          const num = Number(v);
          return Number.isFinite(num) && num >= 0 ? num / 100 : defaultPct / 100;
        };
        const rateLow = parseRate(g.manager.override_bonus_rate_low, 4);
        const rateHigh = parseRate(g.manager.override_bonus_rate_high, 6);

        // Convert to USD for threshold comparison
        const totalPlanUsd = totalOtherManagersPlan / usdToUah;

        let overrideRate = 0;
        if (totalPlanUsd >= 50000) {
          overrideRate = rateHigh;
        } else if (totalPlanUsd >= 10000) {
          overrideRate = rateLow;
        }
        overrideExpected = totalOtherManagersPlan * overrideRate;

        const totalFactUsd = totalOtherManagersFact / usdToUah;
        let overrideRateFact = 0;
        if (totalFactUsd >= 50000) {
          overrideRateFact = rateHigh;
        } else if (totalFactUsd >= 10000) {
          overrideRateFact = rateLow;
        }
        overrideBonus = totalOtherManagersFact * overrideRateFact;
      }
      
      result[g.manager.id] = {
        expectedBonus,
        realBonus,
        overrideExpected,
        overrideBonus,
        totalExpected: expectedBonus + overrideExpected,
        totalReal: realBonus + overrideBonus,
      };
    });
    
    return result;
  }, [projectsByManager, usdToUah]);

  const toggleManager = (id) => {
    setExpandedManagers(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleProject = (id) => {
    setExpandedProjects(prev => ({ ...prev, [id]: !prev[id] }));
  };
  
  // State for showing closed projects per manager
  const [showClosedFor, setShowClosedFor] = useState({});

  // Sort projects by client name
  const sortByClient = useCallback((projectsList) => {
    if (!clientSort) return projectsList;
    return [...projectsList].sort((a, b) => {
      const nameA = (a.client_name || "").toLowerCase();
      const nameB = (b.client_name || "").toLowerCase();
      return clientSort === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  }, [clientSort]);

  const updateProjectStatus = async (projectId, status) => {
    try {
      await unifiedProjectsApi.update(projectId, { status: status });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const getStatusBadge = (status, projectId = null) => {
    const s = DEAL_STATUSES.find(d => d.value === status);
    if (!s) return null;
    
    if (projectId) {
      return (
        <Select value={status} onValueChange={(v) => updateProjectStatus(projectId, v)}>
          <SelectTrigger className={cn("h-7 w-[100px] text-xs border", s.color, "bg-transparent")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            {DEAL_STATUSES.map(st => (
              <SelectItem key={st.value} value={st.value} className="text-zinc-50 text-xs">{st.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    return <Badge className={cn("text-xs border", s.color)}>{s.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="sales-dashboard" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Sales Projects</h1>
          <p className="text-zinc-500 text-sm">
            {viewMode === "realistic"
              ? "Realistic forecast — production-phase payments dated from task deadlines"
              : "All planned payments — manager-set months (wishful view)"}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Realistic/All toggle — marker: realistic-forecast-v1 */}
          <div className="flex items-center gap-1 bg-zinc-800/50 rounded-lg p-1" data-testid="view-mode-toggle">
            {VIEW_MODES.map(m => (
              <button
                key={m.value}
                onClick={() => setViewMode(m.value)}
                data-testid={`view-mode-${m.value}`}
                className={cn(
                  "h-8 px-3 text-xs font-medium rounded-md transition-colors",
                  viewMode === m.value
                    ? "bg-orange-500 text-white"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-700/50"
                )}
              >
                {m.label}
              </button>
            ))}
          </div>

          <Button
            onClick={() => setShowNewProjectDialog(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="new-project-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>

          <div className="flex items-center gap-2 bg-zinc-800/50 rounded-lg p-1">
            <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)} className="h-8 w-8">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-zinc-50 font-medium px-3 min-w-[140px] text-center">{formatMonth(selectedMonth)}</span>
            <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)} className="h-8 w-8">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Mode explainer — small, dismissable-feel without state */}
      {viewMode === "realistic" ? (
        <div className="flex items-start gap-3 text-xs px-4 py-3 rounded-lg bg-emerald-950/30 border border-emerald-900/30 text-emerald-200/80">
          <Info className="h-4 w-4 mt-0.5 text-emerald-400 flex-shrink-0" />
          <div className="leading-relaxed">
            <strong className="text-emerald-300">Realistic Forecast</strong> shows payments expected to land in <strong>{formatMonth(selectedMonth)}</strong>.
            Production-phase payments are dated from <code className="bg-emerald-950/50 px-1 rounded">max(task.deadline)</code> on the project (not the manager's wishful month).
            Sales-phase payments (advances/deposits) trust the manager-set month.
            Switch to <em>All Plan</em> to see what each manager originally wrote.
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 text-xs px-4 py-3 rounded-lg bg-amber-950/30 border border-amber-900/30 text-amber-200/80">
          <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-400 flex-shrink-0" />
          <div className="leading-relaxed">
            <strong className="text-amber-300">All Plan view</strong> — payments shown here are what managers <em>hope</em> will land in {formatMonth(selectedMonth)}.
            Production-phase finals may slip to later months in reality. Switch back to <em>Realistic Forecast</em> for derived dates.
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-blue-400" />
              <span className="text-xs text-zinc-500 uppercase">Plan (&gt;50%)</span>
            </div>
            <p className="text-xl font-bold text-blue-400">{formatAmount(monthTotals.planUAH)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-900/20 border-green-800/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-green-400" />
              <span className="text-xs text-green-500 uppercase">Fact (Paid)</span>
            </div>
            <p className="text-xl font-bold text-green-400">{formatAmount(monthTotals.factUAH)}</p>
            <p className="text-xs text-zinc-500">{monthTotals.paidCount} of {monthTotals.servicesCount} payments</p>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 text-zinc-400" />
              <span className="text-xs text-zinc-500 uppercase">Total Pipeline</span>
            </div>
            <p className="text-xl font-bold text-zinc-50">{formatAmount(monthTotals.allUAH)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-900/20 border-orange-800/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-orange-400" />
              <span className="text-xs text-orange-500 uppercase">Pending (&gt;50%)</span>
            </div>
            <p className="text-xl font-bold text-orange-400">{formatAmount(monthTotals.pendingUAH)}</p>
            <p className="text-xs text-zinc-500">{monthTotals.pendingCount} unpaid</p>
          </CardContent>
        </Card>
      </div>

      {/* Projects by Manager */}
      {projectsByManager.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="py-12 text-center text-zinc-500">
            No projects for this month
          </CardContent>
        </Card>
      ) : (
        projectsByManager.map(group => {
          const bonus = bonusData[group.manager.id] || {};
          const isKonstantin = group.manager.name === "Kuzmenko Kostiantyn";
          const hasClosedProjects = group.closedProjects.length > 0;
          const totalProjects = group.activeProjects.length + group.closedProjects.length;
          
          return (
            <Card key={group.manager.id} className="bg-zinc-900/50 border-zinc-800">
              <CardHeader 
                className="cursor-pointer hover:bg-zinc-800/30 transition-colors"
                onClick={() => toggleManager(group.manager.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {expandedManagers[group.manager.id] ? 
                      <ChevronDown className="h-5 w-5 text-zinc-500" /> : 
                      <ChevronRightIcon className="h-5 w-5 text-zinc-500" />
                    }
                    <Building2 className="h-5 w-5 text-orange-400" />
                    <CardTitle className="text-zinc-50">{group.manager.name}</CardTitle>
                    {group.manager.is_partnership && (
                      <Badge className="bg-purple-900/30 text-purple-400 border-purple-800 text-xs">Partnership</Badge>
                    )}
                    <Badge className="bg-zinc-700 text-zinc-300">{totalProjects} projects</Badge>
                    {hasClosedProjects && (
                      <Badge className="bg-green-900/30 text-green-400 border-green-800">{group.closedProjects.length} closed</Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-right">
                      <p className="text-xs text-zinc-500">Plan</p>
                      <p className="font-mono text-blue-400">{formatAmount(group.totalPlanUAH)}</p>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xs text-green-500">Fact</p>
                      <p className="font-mono text-green-400">{formatAmount(group.totalFactUAH)}</p>
                    </div>
                    
                    <div className="flex items-center gap-4 pl-4 border-l border-zinc-700">
                      <div className="text-right">
                        <p className="text-xs text-zinc-500">Expected</p>
                        <p className="font-mono text-zinc-400">{formatAmount(bonus.totalExpected || 0)}</p>
                        {isKonstantin && bonus.overrideExpected > 0 && (
                          <p className="text-xs text-zinc-600">+{formatAmount(bonus.overrideExpected)} override</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-orange-400">Real Bonus</p>
                        <p className="font-mono text-orange-400 font-bold">{formatAmount(bonus.totalReal || 0)}</p>
                        {isKonstantin && bonus.overrideBonus > 0 && (
                          <p className="text-xs text-orange-600">+{formatAmount(bonus.overrideBonus)} override</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>

              {expandedManagers[group.manager.id] && (
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-zinc-400 w-[30px]"></TableHead>
                        <TableHead className="text-zinc-400">Project</TableHead>
                        <TableHead 
                          className="text-zinc-400 cursor-pointer select-none hover:text-orange-400 transition-colors"
                          onClick={toggleClientSort}
                          data-testid="sort-client-header"
                        >
                          <div className="flex items-center gap-1">
                            Client
                            {clientSort === "asc" ? <ArrowUp className="h-3.5 w-3.5 text-orange-400" /> :
                             clientSort === "desc" ? <ArrowDown className="h-3.5 w-3.5 text-orange-400" /> :
                             <ArrowUpDown className="h-3.5 w-3.5 text-zinc-600" />}
                          </div>
                        </TableHead>
                        <TableHead className="text-zinc-400">PM</TableHead>
                        <TableHead className="text-zinc-400 text-center">Status</TableHead>
                        <TableHead className="text-zinc-400 text-center w-[110px]">Month</TableHead>
                        <TableHead className="text-zinc-400 text-right">Plan</TableHead>
                        <TableHead className="text-zinc-400 text-right">Fact</TableHead>
                        <TableHead className="text-zinc-400 text-right">Total</TableHead>
                        <TableHead className="text-zinc-400 text-center">Paid</TableHead>
                        <TableHead className="text-zinc-400 w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Active projects first */}
                      {sortByClient(group.activeProjects).map(project => (
                        <ProjectRow 
                          key={project.id}
                          project={project}
                          isExpanded={expandedProjects[project.id]}
                          onToggle={() => toggleProject(project.id)}
                          onNavigate={() => navigate(`/sales/project/${project.id}`)}
                          onDelete={() => handleDeleteProject(project.id, project.name)}
                          formatAmount={formatAmount}
                          formatOriginal={formatOriginal}
                          getStatusBadge={getStatusBadge}
                          toggleServicePaid={toggleServicePaid}
                          updateServiceMonth={updateServiceMonth}
                          updateServiceProbability={updateServiceProbability}
                          monthOptions={monthOptions}
                        />
                      ))}

                      {/* Closed projects section */}
                      {hasClosedProjects && (
                        <>
                          <TableRow className="border-zinc-800 bg-green-950/10 hover:bg-green-950/20">
                            <TableCell colSpan={10} className="py-2">
                              <button
                                onClick={() => setShowClosedFor(prev => ({ ...prev, [group.manager.id]: !prev[group.manager.id] }))}
                                className="flex items-center gap-2 text-green-400 text-sm"
                              >
                                {showClosedFor[group.manager.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                                <CheckCircle2 className="h-4 w-4" />
                                <span>Closed & Paid ({group.closedProjects.length})</span>
                              </button>
                            </TableCell>
                          </TableRow>
                          
                          {showClosedFor[group.manager.id] && sortByClient(group.closedProjects).map(project => (
                            <TableRow key={project.id} className="border-green-900/20 bg-green-950/20 hover:bg-green-950/30">
                              <TableCell></TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                                  <span className="text-green-300">{project.name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-zinc-400">{project.client_name}</TableCell>
                              <TableCell className="text-zinc-500 text-sm">{project.project_manager_name || "—"}</TableCell>
                              <TableCell className="text-center">
                                <Badge className="bg-green-900/30 text-green-400 border-green-800 text-xs">Paid</Badge>
                              </TableCell>
                              <TableCell className="text-right font-mono text-green-400/70">{formatAmount(project.planUAH)}</TableCell>
                              <TableCell className="text-right font-mono text-green-400">{formatAmount(project.factUAH)}</TableCell>
                              <TableCell className="text-right font-mono text-green-300">{formatAmount(project.allUAH)}</TableCell>
                              <TableCell className="text-center text-green-500">{project.paidCount}/{project.services.length}</TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigate(`/sales/project/${project.id}`)}
                                  className="h-7 w-7 p-0 text-zinc-500 hover:text-green-400"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              )}
            </Card>
          );
        })
      )}
      
      {/* Unassigned Projects Section */}
      {unassignedProjects && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                  <UserX className="h-5 w-5 text-yellow-400" />
                </div>
                <div>
                  <CardTitle className="text-zinc-50 text-lg">Unassigned / Inactive Manager</CardTitle>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {unassignedProjects.activeProjects.length + unassignedProjects.closedProjects.length} projects
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-zinc-500">Plan</p>
                  <p className="text-blue-400 font-mono">{formatAmount(unassignedProjects.totalPlanUAH)}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-zinc-500">Fact</p>
                  <p className="text-green-400 font-mono">{formatAmount(unassignedProjects.totalFactUAH)}</p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="w-[30px]"></TableHead>
                  <TableHead className="text-zinc-400">Project</TableHead>
                  <TableHead 
                    className="text-zinc-400 cursor-pointer select-none hover:text-orange-400 transition-colors"
                    onClick={toggleClientSort}
                  >
                    <div className="flex items-center gap-1">
                      Client
                      {clientSort === "asc" ? <ArrowUp className="h-3.5 w-3.5 text-orange-400" /> :
                       clientSort === "desc" ? <ArrowDown className="h-3.5 w-3.5 text-orange-400" /> :
                       <ArrowUpDown className="h-3.5 w-3.5 text-zinc-600" />}
                    </div>
                  </TableHead>
                  <TableHead className="text-zinc-400">PM</TableHead>
                  <TableHead className="text-zinc-400 text-center">Status</TableHead>
                  <TableHead className="text-zinc-400 text-center w-[110px]">Month</TableHead>
                  <TableHead className="text-zinc-400 text-right">Plan</TableHead>
                  <TableHead className="text-zinc-400 text-right">Fact</TableHead>
                  <TableHead className="text-zinc-400 text-right">Total</TableHead>
                  <TableHead className="text-zinc-400 text-center">Paid</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortByClient(unassignedProjects.activeProjects).map(project => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    isExpanded={expandedProjects[project.id]}
                    onToggle={() => toggleProject(project.id)}
                    onNavigate={() => navigate(`/sales/project/${project.id}`)}
                    onDelete={() => handleDeleteProject(project.id, project.name)}
                    formatAmount={formatAmount}
                    formatOriginal={formatOriginal}
                    getStatusBadge={getStatusBadge}
                    toggleServicePaid={toggleServicePaid}
                    updateServiceMonth={updateServiceMonth}
                    updateServiceProbability={updateServiceProbability}
                    monthOptions={monthOptions}
                  />
                ))}
                {sortByClient(unassignedProjects.closedProjects).map(project => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    isExpanded={expandedProjects[project.id]}
                    onToggle={() => toggleProject(project.id)}
                    onNavigate={() => navigate(`/sales/project/${project.id}`)}
                    onDelete={() => handleDeleteProject(project.id, project.name)}
                    formatAmount={formatAmount}
                    formatOriginal={formatOriginal}
                    getStatusBadge={getStatusBadge}
                    toggleServicePaid={toggleServicePaid}
                    updateServiceMonth={updateServiceMonth}
                    updateServiceProbability={updateServiceProbability}
                    monthOptions={monthOptions}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      
      {/* New Project Dialog - Using unified component */}
      <CreateProjectDialog
        open={showNewProjectDialog}
        onOpenChange={setShowNewProjectDialog}
        onSuccess={fetchData}
        defaultPhase="sales"
        defaultMonth={selectedMonth}
      />
    </div>
  );
};

// Project row component
const ProjectRow = ({ project, isExpanded, onToggle, onNavigate, onDelete, formatAmount, formatOriginal, getStatusBadge, toggleServicePaid, updateServiceMonth, updateServiceProbability, monthOptions }) => {
  return (
    <React.Fragment>
      <TableRow className={cn("border-zinc-800 hover:bg-zinc-800/50", isExpanded && "bg-zinc-800/30")}>
        <TableCell className="w-[30px] cursor-pointer" onClick={onToggle}>
          {project.services.length > 0 && (
            isExpanded ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRightIcon className="h-4 w-4 text-zinc-500" />
          )}
        </TableCell>
        <TableCell className="cursor-pointer" onClick={onToggle}>
          <div className="flex items-center gap-2">
            <FolderOpen className="h-4 w-4 text-sky-400" />
            <span className="text-zinc-50 font-medium">{project.name || "Untitled"}</span>
            {project.hasNoServices && (
              <Badge className="text-xs bg-yellow-900/30 text-yellow-400">New</Badge>
            )}
          </div>
        </TableCell>
        <TableCell>
          <span className="text-zinc-300">{project.client_name}</span>
        </TableCell>
        <TableCell>
          <span className="text-zinc-500 text-sm">{project.project_manager_name || "—"}</span>
        </TableCell>
        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
          {getStatusBadge(project.status, project.id)}
        </TableCell>
        <TableCell></TableCell>
        <TableCell className="text-right font-mono text-blue-400">
          {project.planUAH > 0 ? formatAmount(project.planUAH) : "—"}
        </TableCell>
        <TableCell className="text-right font-mono text-green-400">
          {project.factUAH > 0 ? formatAmount(project.factUAH) : "—"}
        </TableCell>
        <TableCell className="text-right font-mono text-zinc-50">{formatAmount(project.allUAH)}</TableCell>
        <TableCell className="text-center text-zinc-400">
          {project.paidCount}/{project.services.length}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onNavigate(); }}
              className="h-7 w-7 p-0 text-zinc-400 hover:text-orange-400"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      
      {isExpanded && project.services.map(service => {
        // Realistic-forecast-v1: phase + confidence + month-delta hints
        const phase = service.payment_phase;
        const conf = service.forecast_confidence;
        const realisticMonth = service.realistic_forecast_month;
        const monthsDiffer = realisticMonth && service.month && realisticMonth !== service.month;
        return (
        <TableRow key={service.id} className="border-zinc-800/50 bg-zinc-950/50 hover:bg-zinc-900/30">
          <TableCell></TableCell>
          <TableCell colSpan={3}>
            <div className="flex items-center gap-2 pl-6 flex-wrap">
              <FileText className="h-3 w-3 text-zinc-600" />
              <span className="text-sm text-zinc-300">{service.name}</span>
              {service.payment_group_id && (
                <Badge className="text-xs bg-purple-900/30 text-purple-400 border-purple-700">Split</Badge>
              )}
              {!service.service_type && (
                <span className="flex items-center gap-1 text-xs text-yellow-400" title="Service type not set">
                  <AlertTriangle className="h-3 w-3" />
                </span>
              )}
              {/* Phase badge — marker: realistic-forecast-v1 */}
              {phase === "production" && !service.is_paid && (
                <Badge
                  className="text-xs bg-blue-900/30 text-blue-300 border-blue-800"
                  title={`Production-phase payment — derived from ${service.forecast_source || "production signal"}`}
                >
                  <Wrench className="h-3 w-3 mr-1" /> Production
                </Badge>
              )}
              {phase === "sales" && !service.is_paid && (
                <Badge
                  className="text-xs bg-orange-900/30 text-orange-300 border-orange-800"
                  title="Sales-phase payment (deposit/advance) — manager controls the date"
                >
                  <Target className="h-3 w-3 mr-1" /> Sales
                </Badge>
              )}
              {/* Low/no confidence — explicit warning for production-phase with no signal */}
              {conf === "none" && (
                <Badge
                  className="text-xs bg-red-900/30 text-red-300 border-red-800"
                  title="No production signal — set deadlines on tasks or project.deadline to improve the forecast"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" /> no signal
                </Badge>
              )}
              {conf === "low" && (
                <Badge
                  className="text-xs bg-amber-900/30 text-amber-300 border-amber-800"
                  title={`Low-confidence forecast (source: ${service.forecast_source})`}
                >
                  <CircleDot className="h-3 w-3 mr-1" /> low conf
                </Badge>
              )}
              {/* Manager-month → realistic-month delta indicator */}
              {monthsDiffer && (
                <Badge
                  className="text-xs bg-cyan-900/30 text-cyan-300 border-cyan-800"
                  title={`Manager set ${service.month}, production says ${realisticMonth}`}
                >
                  {service.month} → {realisticMonth}
                </Badge>
              )}
            </div>
          </TableCell>
          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
            <Select
              value={String(service.probability)}
              onValueChange={(v) => updateServiceProbability(service.id, Number(v))}
            >
              <SelectTrigger className={cn(
                "h-6 w-[65px] text-xs border-0",
                service.probability >= 80 ? "bg-green-900/30 text-green-400" :
                service.probability >= 50 ? "bg-blue-900/30 text-blue-400" :
                service.probability >= 30 ? "bg-yellow-900/30 text-yellow-400" :
                "bg-zinc-700/50 text-zinc-400"
              )}>
                <SelectValue>{service.probability}%</SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(p => (
                  <SelectItem key={p} value={String(p)} className="text-zinc-50 text-xs">
                    {p}%
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TableCell>
          <TableCell onClick={(e) => e.stopPropagation()}>
            <Select
              value={service.month || ""}
              onValueChange={(v) => updateServiceMonth(service.id, v)}
            >
              <SelectTrigger className="h-7 w-[100px] bg-zinc-800 border-zinc-700 text-zinc-50 text-xs">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                {monthOptions.map(m => (
                  <SelectItem key={m.value} value={m.value} className="text-zinc-50 text-xs">{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TableCell>
          <TableCell className="text-right font-mono text-sm text-blue-400/70">
            {service.probability > 50 ? formatOriginal(service.amount, service.currency) : "—"}
          </TableCell>
          <TableCell className="text-right font-mono text-sm text-green-400/70">
            {service.is_paid ? formatOriginal(service.amount, service.currency) : "—"}
          </TableCell>
          <TableCell className="text-right font-mono text-sm text-zinc-400">
            {formatOriginal(service.amount, service.currency)}
          </TableCell>
          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center gap-1">
              <Switch
                checked={service.is_paid}
                onCheckedChange={(checked) => toggleServicePaid(service.id, checked)}
                className="data-[state=checked]:bg-green-600 scale-75"
              />
              {service.is_paid ? (
                <CheckCircle2 className="h-3 w-3 text-green-500" />
              ) : (
                <Clock className="h-3 w-3 text-zinc-500" />
              )}
            </div>
          </TableCell>
          <TableCell></TableCell>
        </TableRow>
        );
      })}
    </React.Fragment>
  );
};

export default SalesDashboardPage;
